<template>
  <div class="pa-3">
    <div class="d-flex align-center mb-3">
      <v-icon size="20" color="primary" class="mr-1">mdi-tag-outline</v-icon>
      <span class="font-weight-medium">Tags ของแชทนี้</span>
      <v-spacer />
      <v-btn icon small @click="reload" :loading="loading" title="โหลดใหม่">
        <v-icon size="18">mdi-refresh</v-icon>
      </v-btn>
    </div>

    <div v-if="!chatId" class="caption text-center pa-4" style="color: grey">
      ไม่พบ Tags ของแชทนี้
    </div>

    <template v-else>
      <!-- assigned tags -->
      <div class="mb-3">
        <div v-if="assignedTags.length === 0" class="caption" style="color: grey">
          ยังไม่ได้แท็ก
        </div>
        <v-chip
          v-for="t in assignedTags"
          :key="t.id"
          :color="t.color"
          dark
          close
          small
          :disabled="saving"
          class="mr-1 mb-1"
          @click:close="unassign(t.id)"
        >
          {{ t.name }}
        </v-chip>
      </div>

      <v-divider class="mb-3" />

      <!-- เพิ่ม tag ที่มีอยู่แล้ว -->
      <div class="mb-3">
        <div class="caption mb-1">เพิ่ม tag ที่มี:</div>
        <v-chip
          v-for="t in unassignedTags"
          :key="t.id"
          :color="t.color"
          outlined
          small
          :disabled="saving"
          class="mr-1 mb-1"
          @click="assign(t)"
        >
          <v-icon size="14" left>mdi-plus</v-icon>{{ t.name }}
        </v-chip>
        <div v-if="unassignedTags.length === 0" class="caption" style="color: grey">
          (ไม่มี tag เหลือให้เพิ่ม — สร้างใหม่ด้านล่าง)
        </div>
      </div>

      <v-divider class="mb-3" />

      <!-- สร้าง tag ใหม่ -->
      <div>
        <div class="caption mb-1">สร้าง tag ใหม่:</div>
        <v-text-field
          v-model="newName"
          outlined dense hide-details
          placeholder="ชื่อ tag"
          class="mb-2"
          :disabled="saving"
        />
        <div class="mb-2">
          <span
            v-for="c in colorPresets"
            :key="c"
            class="color-cell"
            :style="{ background: c, border: newColor === c ? '3px solid #000' : '1px solid #ccc' }"
            @click="newColor = c"
          />
        </div>
        <v-btn
          small color="primary"
          :disabled="!newName.trim() || saving"
          :loading="creating"
          @click="createTag"
        >
          <v-icon size="16" left>mdi-plus</v-icon>สร้าง tag
        </v-btn>
      </div>

      <v-divider class="my-3" />

      <!-- จัดการ tag ทั้งหมด -->
      <div>
        <div class="caption mb-1">จัดการ tag ทั้งหมด:</div>
        <v-list dense class="py-0">
          <v-list-item v-for="t in availableTags" :key="t.id" class="px-0">
            <v-chip :color="t.color" dark small>{{ t.name }}</v-chip>
            <v-spacer />
            <v-btn icon small :disabled="saving" @click="deleteTag(t.id)">
              <v-icon size="16" color="error">mdi-delete-outline</v-icon>
            </v-btn>
          </v-list-item>
        </v-list>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { expressService_dotnet } from "@/website/global";
import { token_api } from "@/website/backend/token";

const COLOR_PRESETS = [
  "#e53935", "#fb8c00", "#fdd835", "#43a047",
  "#1e88e5", "#5e35b1", "#8e24aa", "#6d4c41",
];

export default {
  name: "TagPanel",
  props: {
    shopId: { type: [String, Number], required: true },
    staffShopId: { type: [String, Number], default: null },
    chatId: { type: [String, Number], default: null },
  },
  data: () => ({
    tags: [],                // [{id, name, color}, ...]
    assignedIds: [],         // tagIds ของ chat ปัจจุบัน
    newName: "",
    newColor: COLOR_PRESETS[0],
    colorPresets: COLOR_PRESETS,
    loading: false,
    creating: false,
    saving: false,           // กัน assign/unassign/delete ซ้อน
  }),
  computed: {
    availableTags() {
      return this.tags;
    },
    assignedTags() {
      return this.tags.filter((t) => this.assignedIds.includes(t.id));
    },
    unassignedTags() {
      return this.tags.filter((t) => !this.assignedIds.includes(t.id));
    },
    _headers() {
      return { "Content-Type": "application/json", Authorization: "Bearer " + token_api };
    },
  },
  watch: {
    chatId() {
      // โหลด assigned ใหม่เมื่อ chat เปลี่ยน — tag list (this.tags) ใช้ของเดิมได้
      this.loadAssigned();
    },
  },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        await this.loadTags();
        await this.loadAssigned();
        this.emitChange();
      } finally {
        this.loading = false;
      }
    },
    async loadTags() {
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatTag/get-tags",
          { shopId: this.shopId },
          { headers: this._headers }
        );
        this.tags = (res.data && res.data.tags) || [];
      } catch (e) {
        console.warn("[TagPanel] loadTags error:", e.message || e);
      }
    },
    async loadAssigned() {
      if (!this.chatId) {
        this.assignedIds = [];
        return;
      }
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatTag/get-chat-tags",
          { shopId: this.shopId, chatId: parseInt(this.chatId) },
          { headers: this._headers }
        );
        const list = (res.data && res.data.tags) || [];
        this.assignedIds = list.map((t) => t.id);
      } catch (e) {
        console.warn("[TagPanel] loadAssigned error:", e.message || e);
      }
    },
    // ส่ง map ของทุก chat ของ shop กลับให้ parent (สำหรับ tagsByChat ใน ChatTitleList)
    async emitChange() {
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatTag/get-all-chat-tags",
          { shopId: this.shopId },
          { headers: this._headers }
        );
        const byChat = (res.data && res.data.byChat) || {};
        this.$emit("change", { tags: this.tags, byChat });
      } catch (e) {
        console.warn("[TagPanel] emitChange error:", e.message || e);
      }
    },
    async createTag() {
      const name = this.newName.trim();
      if (!name) return;
      this.creating = true;
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatTag/create-tag",
          {
            shopId: this.shopId,
            name,
            color: this.newColor,
            staffShopId: this.staffShopId,
          },
          { headers: this._headers }
        );
        if (res.data && res.data.status === "success" && res.data.tag) {
          this.tags.unshift(res.data.tag);
          this.newName = "";
          this.emitChange();
        } else {
          this.$swal && this.$swal({
            icon: "error",
            title: "สร้าง tag ไม่สำเร็จ",
            text: (res.data && res.data.message) || "",
          });
        }
      } catch (e) {
        this.$swal && this.$swal({
          icon: "error",
          title: "สร้าง tag ไม่สำเร็จ",
          text: e.message || "",
        });
      } finally {
        this.creating = false;
      }
    },
    async deleteTag(id) {
      this.saving = true;
      try {
        const res = await axios.post(
          expressService_dotnet + "ChatTag/delete-tag",
          { id, shopId: this.shopId },
          { headers: this._headers }
        );
        if (res.data && res.data.status === "success") {
          this.tags = this.tags.filter((t) => t.id !== id);
          this.assignedIds = this.assignedIds.filter((x) => x !== id);
          this.emitChange();
        }
      } catch (e) {
        console.warn("[TagPanel] deleteTag error:", e.message || e);
      } finally {
        this.saving = false;
      }
    },
    async assign(tag) {
      if (!this.chatId || this.assignedIds.includes(tag.id)) return;
      this.saving = true;
      const next = this.assignedIds.concat(tag.id);
      try {
        await this._setChatTags(next);
        this.assignedIds = next;
        this.emitChange();
      } finally {
        this.saving = false;
      }
    },
    async unassign(tagId) {
      if (!this.chatId) return;
      this.saving = true;
      const next = this.assignedIds.filter((x) => x !== tagId);
      try {
        await this._setChatTags(next);
        this.assignedIds = next;
        this.emitChange();
      } finally {
        this.saving = false;
      }
    },
    async _setChatTags(tagIds) {
      await axios.post(
        expressService_dotnet + "ChatTag/set-chat-tags",
        {
          shopId: this.shopId,
          chatId: parseInt(this.chatId),
          tagIds,
          staffShopId: this.staffShopId,
        },
        { headers: this._headers }
      );
    },
  },
};
</script>

<style scoped>
.color-cell {
  display: inline-block;
  width: 24px; height: 24px;
  border-radius: 50%;
  margin-right: 6px;
  cursor: pointer;
  vertical-align: middle;
}
</style>